import { styled } from "@mui/material/styles";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogTitleProps,
  ButtonProps,
} from "@mui/material";

/**
 * Shared styled components for modal dialogs in billet-yard-inventory
 * Following MUI v6 Migration Guide - using styled() for reusable components
 */

// ===========================================
// Dialog Title Components
// ===========================================

interface StyledDialogTitleProps extends DialogTitleProps {
  colorScheme?: "primary" | "error";
}

export const StyledDialogTitle = styled(DialogTitle, {
  shouldForwardProp: (prop) => prop !== "colorScheme",
})<StyledDialogTitleProps>(({ theme, colorScheme = "primary" }) => ({
  backgroundColor:
    colorScheme === "error"
      ? theme.palette.error.main
      : theme.palette.primary.main,
  borderBottom: `1px solid ${theme.palette.billetYard.borders.medium}`,
  padding: "16px 24px",
  "& .MuiTypography-root": {
    fontWeight: "bold",
    color: "white",
    textAlign: "center",
    fontSize: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
  },
}));

// ===========================================
// Dialog Content Components
// ===========================================

export const StyledDialogContent = styled(DialogContent)({
  padding: "24px",
  textAlign: "center",
});

// ===========================================
// Dialog Actions Components
// ===========================================

export const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: "24px",
  justifyContent: "center",
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(3),
  flexWrap: "wrap",
}));

// ===========================================
// Button Components
// ===========================================

export const CancelButton = styled(Button)(({ theme }) => ({
  minWidth: "150px",
  minHeight: "56px",
  fontSize: "18px",
  fontWeight: "bold",
  padding: "16px 32px",
  borderRadius: "8px",
  backgroundColor: theme.palette.billetYard.backgrounds.light,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.billetYard.borders.dark}`,
  boxShadow: "0 4px 14px 0 rgba(158, 158, 158, 0.25)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.grey[300],
  },
}));

export const ConfirmButton = styled(Button)(({ theme }) => ({
  minWidth: "150px",
  minHeight: "56px",
  fontSize: "18px",
  fontWeight: "bold",
  padding: "16px 32px",
  borderRadius: "8px",
  backgroundColor: theme.palette.primary.main,
  color: "white",
  boxShadow: "0 4px 14px 0 rgba(63, 81, 181, 0.25)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
  "&:disabled": {
    backgroundColor: theme.palette.action.disabled,
    color: theme.palette.action.disabled,
  },
}));

export const DangerButton = styled(Button)(({ theme }) => ({
  minWidth: "150px",
  minHeight: "56px",
  fontSize: "18px",
  fontWeight: "bold",
  padding: "16px 32px",
  borderRadius: "8px",
  backgroundColor: theme.palette.error.main,
  color: "white",
  boxShadow: "0 4px 14px 0 rgba(244, 67, 54, 0.25)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
  },
  "&:disabled": {
    backgroundColor: theme.palette.action.disabled,
    color: theme.palette.action.disabled,
  },
}));

// Larger button variant for some modals
export const LargeCancelButton = styled(CancelButton)({
  minWidth: "200px",
  minHeight: "64px",
  fontSize: "20px",
  padding: "16px 48px",
});
